import { readStored, writeStored } from '@/lib/storage'
import { NETWORK_TIMEOUT_MS, withTimeout } from '@/lib/compat'
import { AUTH_MODE, authStatus } from '@/lib/session'

/**
 * Baxış profilləri («Kim baxır?») — usr.profiles.
 *
 * Müqavilə web app-dakı `ProfileFull` (`apps/web/src/lib/me.ts`) ilə eynidir.
 * TV yalnız SEÇİM edir (CRUD deyil) — profil yaratmaq/silmək telefonda/saytda
 * rahatdır, pultla ad yazmaq isə ağırdır.
 */

export interface Profile {
  id: string
  name: string
  avatarUrl: string | null
  isKids: boolean
  maxAgeRating: number | null
  hasPin: boolean
  languageCode: string
}

const SELECTED_KEY = 'kb_tv_profile'

/** Mock profillər — backend olmadan «Kim baxır?» ekranını göstərmək üçün. */
const MOCK_PROFILES: Profile[] = [
  { id: 'p1', name: 'Turan', avatarUrl: null, isKids: false, maxAgeRating: null, hasPin: false, languageCode: 'az' },
  { id: 'p2', name: 'Aysel', avatarUrl: null, isKids: false, maxAgeRating: null, hasPin: false, languageCode: 'az' },
  { id: 'p3', name: 'Uşaqlar', avatarUrl: null, isKids: true, maxAgeRating: 6, hasPin: false, languageCode: 'az' },
]

/**
 * Profil sorğusunun NƏTİCƏSİ — status ATILMIR.
 *
 * ⚠ NİYƏ AYRICA TİP (2026-07-31): əvvəl yalnız `Profile[]` qaytarılırdı və 401
 *   (sessiya öldü / refresh alınmadı) BOŞ SİYAHIYA çevrilirdi. Ekran isə boş
 *   siyahını «bu istifadəçinin profili yoxdur» kimi oxuyub «Saytda profil yaradın»
 *   yazırdı — halbuki əsl səbəb sessiyanın bitməsi idi və istifadəçinin görməli
 *   olduğu mesaj «QR ilə yenidən bağlayın»dır. Brauzerdə ölçüldü: etibarsız refresh
 *   token → `POST /api/auth/refresh` 401 → sessiya silinir → ekranda YANILDICI
 *   «profil tapılmadı» mesajı qalırdı.
 */
export type ProfilesResult =
  | { status: 'ok'; profiles: Profile[] }
  /** 401 — sessiya yoxdur və ya yenilənə bilmədi; yeganə çıxış yolu QR ilə yenidən bağlamaqdır. */
  | { status: 'signedOut' }
  /** Şəbəkə/server xətası — sessiya sağdır, sadəcə sorğu alınmadı. */
  | { status: 'error' }

/**
 * Baxış profillərini çəkir.
 * ⚠ Cavab 401-i BOŞ SİYAHIDAN ayırır: «sessiya bitib» ilə «profil yoxdur»
 * fərqli mesaj tələb edir (2026-07-31 qüsuru — istifadəçi olmayan problemi
 * həll etməyə göndərilirdi).
 */
export function getProfilesResult(): Promise<ProfilesResult> {
  if (AUTH_MODE === 'mock') return Promise.resolve<ProfilesResult>({ status: 'ok', profiles: MOCK_PROFILES })

  // ⚠ REAL rejim: `/api/me/profiles` AUTH tələb edir. Əvvəl `httpJson` (auth-suz)
  //   işlədilirdi → 401 → MOCK profillərə düşürdü; MOCK id-si («p1») GUID olmadığı
  //   üçün playback bileti 400/404 verir və HƏR film «hazır deyil» olur. İndi token
  //   ilə gedir və HƏQİQİ profil id-ləri (UUID) qaytarılır. Real rejimdə MOCK-a
  //   DÜŞMÜRÜK — boş siyahı «profil yarat» mesajına aparır (yanlış id playback-i sındırmasın).
  //
  // ⚠ `authStatus` (session.ts) — xam token DEYİL: access token 15 dəqiqədə bitir və
  //   əvvəl bu sorğu səssizcə 401 alıb BOŞ siyahı qaytarırdı (2026-07-31 qüsuru).
  return withTimeout(authStatus('/api/me/profiles'), NETWORK_TIMEOUT_MS, 'me/profiles')
    .then((response): ProfilesResult => {
      if (response.status === 401 || response.status === 403) return { status: 'signedOut' }
      if (response.status < 200 || response.status >= 300) return { status: 'error' }
      try {
        return { status: 'ok', profiles: (JSON.parse(response.text) as Profile[]) || [] }
      } catch {
        return { status: 'error' }
      }
    })
    .catch((): ProfilesResult => ({ status: 'error' }))
}

/**
 * PIN UZUNLUQLARI — serverdəki `UserRules` ilə EYNİ (4 və ya 6 rəqəm).
 *
 * ⚠ Sahib tələbi 2026-08-26: «pin qoyula bilsin 4 rəqəmli və ya 6 rəqəmli».
 * ⚠ ARALIQ DEYİL, İKİ DƏYƏR — 5 rəqəm «yarımçıq yazdım» halını səhvsiz keçirərdi.
 */
export var PIN_SHORT_LENGTH = 4
export var PIN_LONG_LENGTH = 6

/** PIN uzunluğu qanunidirmi. */
export function isValidPinLength(length: number): boolean {
  return length === PIN_SHORT_LENGTH || length === PIN_LONG_LENGTH
}

/**
 * PIN-li profilə keçid üçün SERVER yoxlaması (`POST /api/me/profiles/{id}/pin/verify`).
 *
 * ⚠⚠ NİYƏ ƏLAVƏ OLUNDU (sahib tələbi 2026-08-26: «pin qoyulubsa profiledən profilə
 *    keçiddə pin soruşulsun, əsas da uşaq profilindən böyük profilə keçiddə»).
 *    ƏVVƏL veb TV-də PIN ÜMUMİYYƏTLƏ soruşulmurdu — uşaq profili ilə açılmış TV-də
 *    valideynin profilinə bir kliklə keçmək olurdu və bütün uşaq qaydası keçilirdi.
 * ⚠ PIN CİHAZDA SAXLANMIR və client-də müqayisə edilmir — qərar serverindir.
 * ⚠ Mock rejimdə həmişə `true`: orada backend yoxdur, ekranı bloklamağın mənası yoxdur.
 */
export function verifyProfilePin(id: string, pin: string): Promise<boolean> {
  if (AUTH_MODE === 'mock') return Promise.resolve(true)
  return withTimeout(
    authStatus('/api/me/profiles/' + id + '/pin/verify', {
      method: 'POST',
      body: JSON.stringify({ pin: pin }),
    }),
    NETWORK_TIMEOUT_MS,
    'profiles/pin/verify',
  )
    .then((response) => response.status >= 200 && response.status < 300)
    .catch(() => false)
}

/** Sadə siyahı — status maraqlandırmayan çağıranlar üçün (məs. aktiv profilin adı). */
export function getProfiles(): Promise<Profile[]> {
  return getProfilesResult().then((result) => (result.status === 'ok' ? result.profiles : []))
}

/**
 * Seçilmiş profil id-si HƏQİQİ (UUID) formatındadırmı.
 * Köhnə MOCK seçimini («p1») rədd edir — real rejimə keçəndə həmin id playback-i
 * sındırardı (backend `Profiles.NotFound` → «hazır deyil»). Yenidən seçməyə göndərir.
 */
export function isRealProfileId(id: string | null): boolean {
  if (!id) return false
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)
}

/** Seçilmiş baxış profilini oxuyur. */
export function readSelectedProfileId(): string | null {
  try {
    return readStored(SELECTED_KEY)
  } catch {
    return null
  }
}

const SELECTED_NAME_KEY = 'kb_tv_profile_name'

/**
 * Seçilmiş profilin ADI — uşaq ana ekranının salamlaması («Salam, Ləman!»).
 *
 * ⚠ NİYƏ SAXLANILIR, PROFİL SİYAHISINDAN OXUNMUR: ana ekran SİNXRON qurulur
 *   (fokus arifmetikası, bax `screens/kidsHome.ts`) — profil sorğusunu gözləsəydi
 *   ekranın açılması gecikərdi. Ad `applyViewerAgeLimit` ilə EYNİ ANDA yazılır,
 *   yəni seçimlə sinxrondan çıxa bilmir.
 */
export function readSelectedProfileName(): string | null {
  try {
    return readStored(SELECTED_NAME_KEY)
  } catch {
    return null
  }
}

/** Seçilmiş baxış profilini yadda saxlayır. */
export function writeSelectedProfileId(id: string | null): void {
  try {
    writeStored(SELECTED_KEY, id)
  } catch {
    // Storage bağlıdırsa seçim yalnız bu sessiyada qalır.
  }
}

const MAX_AGE_KEY = 'kb_tv_max_age'

/**
 * UŞAQ PROFİLİNİN YAŞ HƏDDİ — kataloq sorğularının `maxAge` parametri.
 *
 * ⚠⚠ NİYƏ VAR (sahib tələbi 2026-08-26): «uşaq profili ilə giriş edilirsə, ancaq 16
 *    yaşdan aşağı kontent görünməlidir; 16+ və üzəri ÜMUMİYYƏTLƏ görünməməlidir».
 *    ƏVVƏL yaş qapısı YALNIZ oynatma anında işləyirdi — uşaq 18+ filmin kartını,
 *    afişasını və treylerini görürdü, sadəcə «İzlə» deyəndə rədd cavabı alırdı.
 * ⚠ NİYƏ AYRICA AÇAR, profil obyektindən oxumaq YOX: `compat.httpStatus` transport
 *   qatıdır və profil modulunu import etsə dövrə yaranar (profil sorğusu da oradan
 *   gedir). `localStorage` hər iki tərəfin gördüyü ən sadə ortaq nöqtədir.
 */
export function readViewerMaxAge(): number | null {
  try {
    const raw = readStored(MAX_AGE_KEY)
    if (!raw) return null
    const value = parseInt(raw, 10)
    return isFinite(value) ? value : null
  } catch {
    return null
  }
}

/**
 * Həddi yazır və `<html data-kids>` atributunu qurur.
 *
 * ⚠ ATRIBUT TEMANI DA AÇIR: `styles/tv.css` (cihaz) və brauzer variantı bütün
 *   rəngləri token dəyişənlərindən oxuyur, yəni `[data-kids]` bloku bir yerdə
 *   bütün ekranları uşaq dizaynına keçirir.
 */
export function writeViewerMaxAge(maxAge: number | null): void {
  try {
    writeStored(MAX_AGE_KEY, maxAge === null ? null : String(maxAge))
  } catch {
    // Storage bağlıdırsa yalnız bu sessiyada qalır.
  }
  const root = document.documentElement
  if (maxAge === null) root.removeAttribute('data-kids')
  else root.setAttribute('data-kids', String(maxAge))
}

/**
 * Açılışda saxlanmış həddi `<html data-kids>`-ə geri qoyur.
 *
 * ⚠ NİYƏ LAZIMDIR: atribut DOM-dadır, yəni səhifə yenilənəndə itir; hədd isə
 *   `localStorage`-də qalır. Bu olmasa app yenidən açılanda uşaq profili adi
 *   dizaynla və (ilk kadrda) süzülməmiş kartlarla görünərdi.
 */
export function restoreViewerAgeLimit(): void {
  writeViewerMaxAge(readViewerMaxAge())
}

/**
 * Seçilmiş profildən həddi hesablayıb yayır — profil seçimi ekranı çağırır.
 * Uşaq profilində hədd `maxAgeRating`-dir (boşdursa serverin defoltu 12).
 */
export function applyViewerAgeLimit(profile: Profile | null): void {
  writeViewerMaxAge(profile && profile.isKids ? (profile.maxAgeRating === null ? 12 : profile.maxAgeRating) : null)
  // Salamlama üçün ad — izahı `readSelectedProfileName`-dədir.
  try {
    writeStored(SELECTED_NAME_KEY, profile ? profile.name : null)
  } catch {
    // Storage bağlıdırsa salamlama adsız qalır.
  }
}

/**
 * Aktiv profil UŞAQ profilidirmi — ana ekranın hansı maketdə qurulacağını bu həll edir.
 *
 * ⚠ MƏNBƏ `data-kids` ATRİBUTUDUR: onu YALNIZ uşaq profili qoyur (`writeViewerMaxAge`
 *   həddi yalnız `isKids` olanda alır — bax `applyViewerAgeLimit` və `ageGate.ts`).
 *   Yəni ayrıca bayraq saxlamağa ehtiyac yoxdur və iki dəyər sinxrondan çıxa bilmir.
 * ⚠ ADİ PROFİLƏ YAŞ HƏDDİ QOYULSA DA UŞAQ SAYILMIR: server `UpdateProfile`-də adi
 *   profilə də hədd yazmağa icazə verir; süzgəc həddə baxır, MAKET isə uşaqlığa.
 */
export function isKidsProfile(): boolean {
  return document.documentElement.hasAttribute('data-kids')
}
